import { Field, useField } from "formik";
import React, { useEffect, useState } from "react";

import { Column, Row } from "@gamedev-web/gamedev-sdk-core";

import { useString } from "../../localization/localizationHelpers";
import { FieldErrorMessage, FieldHeader, FieldHeaderProps, FieldHeaderTitle } from "../";
import * as commonStyles from "../commonStyles/commonStyles.module.css";
import { SelectDropdown } from "../SelectDropdown";
import * as styles from "./PlayerModesPicker.module.css";

type PlayerModesPickerProps = Readonly<{
  name: string;
  playerModes: Array<{
    value: string;
    label: string;
    playersPerConsoleOptions: number[];
  }>;
  disabled?: boolean;
}> &
  FieldHeaderProps;

type PlayerModesCheckBoxProps = Readonly<{
  text: string;
  name: string;
  playerModeName: string;
  playerModeValue: string;
  playersPerConsoleName: string;
  playersPerConsoleOptions: number[];
  disabled?: boolean;
  ariaLabel?: string;
}>;

const PlayerModesCheckBox = (props: PlayerModesCheckBoxProps) => {
  const {
    text,
    playersPerConsoleName,
    name,
    playerModeName,
    playerModeValue,
    playersPerConsoleOptions,
    disabled,
    ariaLabel,
  } = props;
  const playersPerConsole = useString("playersPerConsole");
  const [field, _meta, helpers] = useField({ name });
  const [_playerModeNameField, _playerModeNameMeta, playerModeNameHelpers] = useField({
    name: playerModeName,
  });
  const [playersPerConsoleField, _playersPerConsoleMeta, playersPerConsoleHelpers] = useField({
    name: playersPerConsoleName,
  });
  const [checkBoxEnabled, setCheckBoxEnabled] = useState(field.value);

  useEffect(() => {
    helpers.setValue(field.value ?? false);
    playerModeNameHelpers.setValue(playerModeValue);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onClick = () => {
    setCheckBoxEnabled(!checkBoxEnabled);
    playerModeNameHelpers.setValue(playerModeValue);
    if (!playersPerConsoleField.value) {
      playersPerConsoleHelpers.setValue("");
    }
  };

  const onChange = (value: string) => {
    playersPerConsoleHelpers.setValue(parseInt(value, 10));
  };

  return (
    <Row className={commonStyles.slimContainer}>
      <Column>
        <label className={styles.option}>
          <Field
            type="checkbox"
            name={name}
            data-testid={name}
            onClick={onClick}
            disabled={disabled}
          />
          {text}
        </label>
        {checkBoxEnabled && playersPerConsoleOptions.length > 0 && (
          <>
            <Row>
              <Column className={styles.dropdownWrapper}>
                <SelectDropdown
                  className={styles.dropdown}
                  value={String(playersPerConsoleField.value ?? "")}
                  onChange={onChange}
                  disabled={disabled}
                  aria-label={`${ariaLabel ?? text} ${playersPerConsole}`}
                  options={playersPerConsoleOptions.map(option => ({
                    id: String(option),
                    label: String(option),
                  }))}
                />
                <FieldHeaderTitle
                  title={playersPerConsole}
                  isRequiredField={true}
                  titleClassName={styles.playersPerConsoleText}
                />
              </Column>
            </Row>
            <FieldErrorMessage fieldName={playersPerConsoleName} />
          </>
        )}
      </Column>
    </Row>
  );
};

export const PlayerModesPicker = (props: PlayerModesPickerProps) => {
  const { name, playerModes, disabled } = props;
  return (
    <Row className={commonStyles.internalContainer}>
      <Column>
        <FieldHeader titleClassName={styles.optionTitle} {...props} />
        {playerModes.map((playerMode, index) => (
          <PlayerModesCheckBox
            key={`${name}-${index}`}
            text={playerMode.label}
            name={`${name}.${index}.selected`}
            playerModeName={`${name}.${index}.playerMode`}
            playerModeValue={playerMode.value}
            playersPerConsoleName={`${name}.${index}.playersPerConsole`}
            playersPerConsoleOptions={playerMode.playersPerConsoleOptions}
            disabled={disabled}
            ariaLabel={playerMode.label}
          />
        ))}
      </Column>
    </Row>
  );
};
